/**
 * Legal Documents Agent — the Privacy Notice and Terms of Service, served from
 * the backend instead of being bundled into the app.
 *
 * Public (no sign-in needed):
 *  - getPublishedLegalDocument(key) / usePublishedLegalDocument(key): the
 *    current published version for /privacy or /terms. Counsel notes are
 *    already stripped server-side.
 *
 * Admin (`/api/admin/legal-documents`):
 *  - listVersions, getVersion, createDraft, updateDraft, deleteDraft,
 *    publishVersion, and the useAdminLegalDocuments() hook.
 *
 * Errors reject with the Error thrown by APIAgent.request: `err.message` is the
 * backend's `detail` (e.g. the 409 "a draft already exists" text) and
 * `err.status` the HTTP status, so callers can show it inline.
 */

import { useCallback, useEffect, useState } from 'react';
import APIAgent from './apiAgent';

export const PUBLIC_ENDPOINT = '/api/legal/documents';
export const ADMIN_ENDPOINT = '/api/admin/legal-documents';

/** Legal documents can run to tens of KB; give writes a little more room. */
const WRITE_TIMEOUT_MS = 20000;

/**
 * Fetch the current published version of a legal document.
 *
 * Signed-out visitors land on /privacy and /terms from the sign-up form, so
 * this must never trigger APIAgent's 401 "session expired" redirect to "/".
 *
 * @param {'privacy'|'terms'} key - Document key.
 * @returns {Promise<{doc_key: string, version: string, title: string,
 *   body_markdown: string, counsel_approved: boolean, published_at: string}>}
 */
export async function getPublishedLegalDocument(key) {
  return APIAgent.request(
    `${PUBLIC_ENDPOINT}/${encodeURIComponent(key)}`,
    'GET',
    null,
    {},
    { skipAuthRedirect: true, retries: 1 },
  );
}

/**
 * List every version of every legal document, newest first.
 *
 * @returns {Promise<Array<object>>} Version summaries.
 */
export async function listVersions() {
  const data = await APIAgent.request(ADMIN_ENDPOINT, 'GET');
  return Array.isArray(data?.versions) ? data.versions : [];
}

/**
 * Fetch one version including its full `body_markdown` (with counsel notes).
 *
 * @param {number|string} id - Version id.
 * @returns {Promise<object>}
 */
export async function getVersion(id) {
  return APIAgent.request(`${ADMIN_ENDPOINT}/${encodeURIComponent(id)}`, 'GET');
}

/**
 * Start a new draft. Title and body are copied from the current published
 * version server-side when omitted.
 *
 * @param {{doc_key: string, version: string, title?: string,
 *   body_markdown?: string, counsel_approved?: boolean,
 *   change_summary?: string}} payload
 * @returns {Promise<object>} The created draft (summary).
 */
export async function createDraft(payload) {
  return APIAgent.request(ADMIN_ENDPOINT, 'POST', payload, {}, { timeout: WRITE_TIMEOUT_MS });
}

/**
 * Update a draft. Published versions reject with 409.
 *
 * @param {number|string} id - Draft id.
 * @param {object} patch - Any of version, title, body_markdown,
 *   counsel_approved, change_summary.
 * @returns {Promise<object>} The updated draft.
 */
export async function updateDraft(id, patch) {
  return APIAgent.request(
    `${ADMIN_ENDPOINT}/${encodeURIComponent(id)}`,
    'PATCH',
    patch,
    {},
    { timeout: WRITE_TIMEOUT_MS },
  );
}

/**
 * Delete a draft (204). Published versions reject with 409.
 *
 * @param {number|string} id - Draft id.
 * @returns {Promise<null>}
 */
export async function deleteDraft(id) {
  return APIAgent.request(`${ADMIN_ENDPOINT}/${encodeURIComponent(id)}`, 'DELETE');
}

/**
 * Publish a draft: it becomes the live version immediately.
 *
 * @param {number|string} id - Draft id.
 * @returns {Promise<object>} The published version (summary).
 */
export async function publishVersion(id) {
  return APIAgent.request(
    `${ADMIN_ENDPOINT}/${encodeURIComponent(id)}/publish`,
    'POST',
    null,
    {},
    { timeout: WRITE_TIMEOUT_MS },
  );
}

/**
 * Load the published version of a legal document with loading/error state.
 *
 * @param {'privacy'|'terms'} key - Document key.
 * @returns {{data: object|null, loading: boolean, error: Error|null,
 *   notFound: boolean, retry: () => void}}
 */
export function usePublishedLegalDocument(key) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    getPublishedLegalDocument(key)
      .then((doc) => {
        if (!cancelled) setData(doc);
      })
      .catch((err) => {
        if (!cancelled) setError(err || new Error('Failed to load document'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    data,
    loading,
    error,
    notFound: Boolean(error && error.status === 404),
    retry,
  };
}

/**
 * Admin hook: every version of every legal document.
 *
 * @returns {{versions: Array<object>, loading: boolean, error: string|null,
 *   isAdmin: boolean|null, refresh: () => Promise<void>}}
 */
export function useAdminLegalDocuments() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setVersions(await listVersions());
      setIsAdmin(true);
    } catch (err) {
      if (err?.status === 403) {
        setIsAdmin(false);
      } else {
        setError(err?.message || 'Failed to load legal documents');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { versions, loading, error, isAdmin, refresh };
}

const LegalDocumentsAgent = {
  getPublishedLegalDocument,
  listVersions,
  getVersion,
  createDraft,
  updateDraft,
  deleteDraft,
  publishVersion,
};

export default LegalDocumentsAgent;
